import React, { createContext, useContext, useState, useCallback } from "react";
import {
  sendChatMessage,
  getChatHistory,
  getRecentConversation,
  type ChatMessage,
  type ChatContext as ChatContextType,
} from "@/lib/chatApi";

// =============================================================================
// TYPES
// =============================================================================

interface Technique {
  id: string;
  name: string;
  category?: string;
  description?: string;
}

interface ChatState {
  isOpen: boolean;
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  conversationId: string | null;
  remainingMessages: number | null;
  context: ChatContextType;
}

interface ChatContextValue extends ChatState {
  technique: Technique | null;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  sendMessage: (content: string) => Promise<void>;
  clearHistory: () => void;
  setContext: (ctx: Partial<ChatContextType>) => void;
  loadConversation: (conversationId: string) => Promise<void>;
}

// =============================================================================
// CONTEXT
// =============================================================================

const TechniqueChatContext = createContext<ChatContextValue | null>(null);

// =============================================================================
// PROVIDER
// =============================================================================

interface TechniqueChatProviderProps {
  technique: Technique | null;
  children: React.ReactNode;
}

export function TechniqueChatProvider({
  technique,
  children,
}: TechniqueChatProviderProps) {
  const [state, setState] = useState<ChatState>({
    isOpen: false,
    messages: [],
    isLoading: false,
    error: null,
    conversationId: null,
    remainingMessages: null,
    context: {},
  });

  const openChat = useCallback(async () => {
    setState((prev) => ({ ...prev, isOpen: true }));

    // Try to load recent conversation if we have a technique
    if (technique?.id && !state.conversationId && state.messages.length === 0) {
      const recentConvo = await getRecentConversation(technique.id);
      if (recentConvo && recentConvo.messageCount > 0) {
        const history = await getChatHistory(recentConvo.id);
        setState((prev) => ({
          ...prev,
          conversationId: recentConvo.id,
          messages: history,
        }));
      }
    }
  }, [technique?.id, state.conversationId, state.messages.length]);

  const closeChat = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const toggleChat = useCallback(() => {
    if (state.isOpen) {
      closeChat();
    } else {
      openChat();
    }
  }, [state.isOpen, openChat, closeChat]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!technique?.id || !content.trim()) return;

      const userMessage: ChatMessage = {
        role: "user",
        content: content.trim(),
        timestamp: new Date().toISOString(),
      };

      // Add user message immediately
      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, userMessage],
        isLoading: true,
        error: null,
      }));

      try {
        const response = await sendChatMessage({
          techniqueId: technique.id,
          message: content.trim(),
          conversationId: state.conversationId || undefined,
          context: state.context,
        });

        if (!response.success) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: response.error || "Failed to get response",
          }));
          return;
        }

        const assistantMessage: ChatMessage = {
          role: "assistant",
          content: response.reply || "",
          timestamp: new Date().toISOString(),
        };

        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, assistantMessage],
          isLoading: false,
          conversationId: response.conversationId || prev.conversationId,
          remainingMessages:
            response.remainingMessages ?? prev.remainingMessages,
        }));
      } catch (err: any) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: err.message || "An unexpected error occurred",
        }));
      }
    },
    [technique?.id, state.conversationId, state.context],
  );

  const clearHistory = useCallback(() => {
    setState((prev) => ({
      ...prev,
      messages: [],
      conversationId: null,
      error: null,
    }));
  }, []);

  const setContext = useCallback((ctx: Partial<ChatContextType>) => {
    setState((prev) => ({
      ...prev,
      context: { ...prev.context, ...ctx },
    }));
  }, []);

  const loadConversation = useCallback(async (conversationId: string) => {
    const history = await getChatHistory(conversationId);
    setState((prev) => ({
      ...prev,
      conversationId,
      messages: history,
    }));
  }, []);

  const value: ChatContextValue = {
    ...state,
    technique,
    openChat,
    closeChat,
    toggleChat,
    sendMessage,
    clearHistory,
    setContext,
    loadConversation,
  };

  return (
    <TechniqueChatContext.Provider value={value}>
      {children}
    </TechniqueChatContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useTechniqueChat() {
  const context = useContext(TechniqueChatContext);

  if (!context) {
    throw new Error(
      "useTechniqueChat must be used within a TechniqueChatProvider",
    );
  }

  // Helper functions
  const askAboutStep = (stepNumber: number, stepTitle?: string) => {
    context.sendMessage(
      `Can you explain step ${stepNumber}${stepTitle ? ` (${stepTitle})` : ""} in more detail?`,
    );
  };

  const askForHint = () => {
    context.sendMessage("Can you give me a hint for this problem?");
  };

  const explainMistake = (userAnswer: string, correctAnswer: string) => {
    context.sendMessage(
      `I got ${userAnswer} but the answer is ${correctAnswer}. Where did I go wrong?`,
    );
  };

  const askForExample = () => {
    context.sendMessage("Can you show me another example of this technique?");
  };

  return {
    ...context,
    askAboutStep,
    askForHint,
    explainMistake,
    askForExample,
  };
}
